import {
    SetStateAction,
    useContext,
    useEffect,
    useState,
    useRef,
    useCallback,
} from 'react'
import styled from 'styled-components'
import {getCurrentColors} from '../../../../../utils/colors'
import {HistoryContext} from '../../../../../contexts/history'
import {InputAdornment, TextField} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import {AssistantApi} from '../../../../../services/AssistantApi.service'
import {threadType} from '../../../../../types/thread'
import ListItem from './ListItem'
import AttachFileIcon from '@mui/icons-material/AttachFile'

const COLORS = getCurrentColors() // Importando as cores do tema atual

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`

const Title = styled.h2`
    font-size: 20px;
    font-weight: 400;
    margin: 15px 0;
    text-align: center;
    color: ${COLORS.cinzaEscuro};
`

const List = styled.ul`
    list-style: none;
    max-height: 300px;
    overflow-y: scroll;
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0;
    margin: 0;
`

const Loader = styled.div`
    text-align: center;
    padding: 10px;
    color: ${COLORS.cinzaEscuro};
`

const DateHeader = styled.div`
    padding: 8px 16px;
    color: ${COLORS.secundariaEscura};
    background-color: #ffffff;
    font-size: 12px;
    font-weight: bold;
    width: 100%;
    position: sticky;
    top: 0;
    z-index: 1;
    weight: 500;
`

export default function History() {
    const {
        historyArray: contextHistoryArray,
        selected,
        setSelected,
    } = useContext(HistoryContext)

    const [searchTerm, setSearchTerm] = useState('')
    const [historyPage, setHistoryPage] = useState(1)
    const [historyArray, setHistoryArray] = useState<threadType[]>([])

    const limit = 15
    const loaderRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setHistoryArray(contextHistoryArray)
    }, [contextHistoryArray])

    const handleSearchChange = (event: {
        target: {value: SetStateAction<string>}
    }) => {
        setSearchTerm(event.target.value)
    }

    const loadMoreItems = useCallback(async () => {
        const api = new AssistantApi()
        const newThreads = await api.getThreads(limit, historyPage * limit)

        if (newThreads.length > 0) {
            setHistoryArray((prevArray) => [...prevArray, ...newThreads])
            setHistoryPage((prevPage) => prevPage + 1)
        }
    }, [historyPage, limit])

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreItems()
                }
            },
            {threshold: 1.0}
        )

        const currentLoaderRef = loaderRef.current
        if (currentLoaderRef) {
            observer.observe(currentLoaderRef)
        }

        return () => {
            if (currentLoaderRef) {
                observer.unobserve(currentLoaderRef)
            }
        }
    }, [loadMoreItems])

    const groupItemsByDate = (items: threadType[]) => {
        const DAYS_PT = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado']
        const grouped: Record<string, threadType[]> = {}
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

        items.forEach((item) => {
            const itemDate = new Date(item.last_updated)
            const itemDay = new Date(
                itemDate.getFullYear(),
                itemDate.getMonth(),
                itemDate.getDate()
            )

            let dateKey = ''
            const diffDays = Math.floor(
                (today.getTime() - itemDay.getTime()) / (1000 * 60 * 60 * 24)
            )

            if (item.name === 'Novo Chat') {
                return
            } else if (diffDays === 0) {
                dateKey = 'Hoje'
            } else if (diffDays === 1) {
                dateKey = 'Ontem'
            } else if (diffDays < 7) {
                dateKey = DAYS_PT[itemDate.getDay()]
            } else if (diffDays < 14) {
                dateKey = 'Semana passada'
            } else if (itemDate.getFullYear() === now.getFullYear()) {
                dateKey = itemDate.toLocaleDateString('pt-BR', {month: 'long'})
                dateKey = dateKey.charAt(0).toUpperCase() + dateKey.slice(1)
            } else {
                dateKey = itemDate.getFullYear().toString()
            }

            if (!grouped[dateKey]) {
                grouped[dateKey] = []
            }
            grouped[dateKey].push(item)
        })

        const orderedGroups = new Map<string, threadType[]>()

        // Hoje e Ontem
        ;['Hoje', 'Ontem'].forEach((key) => {
            if (grouped[key]) orderedGroups.set(key, grouped[key])
        })

        // Dias da semana em ordem regressiva (anteontem até 6 dias atrás)
        for (let d = 2; d <= 6; d++) {
            const date = new Date(today)
            date.setDate(date.getDate() - d)
            const key = DAYS_PT[date.getDay()]
            if (grouped[key] && !orderedGroups.has(key)) {
                orderedGroups.set(key, grouped[key])
            }
        }

        if (grouped['Semana passada']) {
            orderedGroups.set('Semana passada', grouped['Semana passada'])
        }

        const months = [
            'Janeiro',
            'Fevereiro',
            'Março',
            'Abril',
            'Maio',
            'Junho',
            'Julho',
            'Agosto',
            'Setembro',
            'Outubro',
            'Novembro',
            'Dezembro',
        ]

        const currentMonth = now.getMonth()
        for (let i = currentMonth; i >= 0; i--) {
            const monthKey = months[i]
            if (grouped[monthKey]) {
                orderedGroups.set(monthKey, grouped[monthKey])
            }
        }

        // Adiciona os anos anteriores em ordem decrescente
        Object.keys(grouped)
            .filter(
                (key) =>
                    !isNaN(Number(key)) &&
                    !months.includes(key)
            )
            .sort((a, b) => Number(b) - Number(a))
            .forEach((year) => {
                orderedGroups.set(year, grouped[year])
            })

        return orderedGroups
    }

    const filteredHistory = historyArray
        .filter((item) => {
            return item.name.toLowerCase().includes(searchTerm.toLowerCase())
        })
        .slice(0, historyPage * limit)

    const groupedHistory = groupItemsByDate(filteredHistory)

    return (
        <Container>
            {/* <Title>Histórico de Consultas</Title> */}
            <TextField
                size="small"
                variant="outlined"
                placeholder="Procure sua consulta..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    mb: 2,
                    mx: 2,
                    '& .MuiInputBase-input': {
                        fontSize: '13px',
                    },
                    '& .MuiInputBase-input::placeholder': {
                        fontSize: '13px',
                    },
                }}
            />
            <List>
                {Array.from(groupedHistory.entries()).map(([date, items]) => (
                    <div key={date} style={{width: '100%'}}>
                        <DateHeader>{date}</DateHeader>
                        {items.map(
                            (item) =>
                                item.name !== 'Novo Chat' && (
                                    <div
                                        key={item.guid}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <ListItem
                                            key={item.guid}
                                            item={item}
                                            isSelected={
                                                selected?.guid === item.guid
                                            }
                                            onSelect={() => setSelected(item)}
                                        />
                                    </div>
                                )
                        )}
                    </div>
                ))}
                <Loader ref={loaderRef}></Loader>
            </List>
        </Container>
    )
}
